import type { EventLogTypeGroup } from 'shared-types';

/**
 * Every event type there is and the groups it belongs to. Source of truth for
 * the EventLogType type and for the event lists returned by getLimitedEventsByTypeGroup.
 * An empty array means the event type is purposefully ungrouped and
 * shows up only in the unfiltered event log.
 */
const eventTypeGroups = {
  'accept-individual-achievement-request': ['achievement'],
  'achievement-access-denied': ['achievement'],
  'achievement-completion-closing-approval': ['achievement'],
  'achievement-completion-conversation-closed': ['achievement'],
  'activate-shared-two-factor-authentication': ['changelog'],
  'activate-two-factor-authentication': ['changelog'],
  'add-achievement-admin': ['achievement'],
  'add-achievement-completions': ['achievement'],
  'add-achievement-owner': ['achievement'],
  'add-admin-group-user': ['admin'],
  'add-calendar-event-attachment': ['calendar'],
  'add-calendar-event-feedback-question': ['calendar'],
  'add-calendar-event-feedback-template': ['calendar'],
  'add-calendar-event-feedback-template-question': ['calendar'],
  'add-calendar-event-owner': ['calendar'],
  'add-calendar-event-participation': ['calendar'],
  'add-calendar-event-self': ['calendar'],
  'add-calendar-event-users': ['calendar'],
  'add-client-page-section': ['clientPage'],
  'add-client-page-section-attachment': ['clientPage'],
  'add-competency-admin': ['competency'],
  'add-competency-endorsement': ['changelog', 'competency'],
  'add-competency-profile-admin': ['competencyProfile'],
  'add-course-admin': ['course'],
  'add-course-language-version': ['course'],
  'add-course-owner': ['course'],
  'add-course-users': ['course'],
  'add-email-message-template-product': [],
  'add-group-admin': ['group'],
  'add-group-course': ['group'],
  'add-group-owner': ['group'],
  'add-group-to-supervisor': ['admin'],
  'add-group-users': ['group'],
  'add-path-group': ['path'],
  'add-path-owner': ['path'],
  'add-path-step': ['path'],
  'add-path-users': ['path'],
  'add-supervisees': ['admin'],
  'add-user-competencies': ['changelog', 'competency'],
  'allow-new-course-completion-attempt': ['course'],
  'apply-calendar-event-feedback-template': ['calendar'],
  'calendar-event-access-denied': ['calendar'],
  'change-supervisor': ['admin'],
  'competency-access-denied': ['competency'],
  'complete-course': ['course'],
  'complete-course-failed': ['course'],
  'complete-course-waiting': ['course'],
  'complete-path': ['path'],
  'copy-calendar-event': ['calendar'],
  'copy-chapter': ['course'],
  'copy-competency': ['competency'],
  'copy-course': ['course'],
  'copy-path': ['path'],
  'course-access-denied': ['course'],
  'create-achievement': ['achievement'],
  'create-achievement-product-user-custom-attribute': ['achievement'],
  'create-admin-group': ['admin'],
  'create-answer': ['course'],
  'create-answer-review': ['course'],
  'create-auth-strategy': ['authStrategies', 'integrationsSharedChangelog'],
  'create-calendar-event': ['calendar'],
  'create-calendar-event-product-user-custom-attribute': ['calendar'],
  'create-chapter': ['course'],
  'create-chat-message': ['course'],
  'create-competency': ['competency'],
  'create-competency-profile': ['competencyProfile'],
  'create-course': ['course'],
  'create-discussion-comment': ['course'],
  'create-email-message-template': [],
  'create-feedback': ['course'],
  'create-group': ['group'],
  'create-h5p-status': ['course'],
  'create-individual-achievement': ['achievement'],
  'create-integration': ['integrationChangelog', 'integrationsSharedChangelog'],
  'create-path': ['path'],
  'create-scorm-status': ['course'],
  'create-supervisor': ['admin', 'changelog'],
  'create-user': ['changelog'],
  'delete-account': ['changelog'],
  'delete-achievement': ['achievement'],
  'delete-admin-group': ['admin'],
  'delete-all-admin-groups': ['admin'],
  'delete-all-competencies': ['competency'],
  'delete-all-competency-profiles': ['competencyProfile'],
  'delete-answer': ['course'],
  'delete-answer-review': ['course'],
  'delete-auth-strategy': ['authStrategies', 'integrationsSharedChangelog'],
  'delete-calendar-event': ['calendar'],
  'delete-chapter': ['course'],
  'delete-chat-messages': ['course'],
  'delete-client-page-section': ['clientPage'],
  'delete-competency': ['competency'],
  'delete-competency-endorsement': ['changelog', 'competency'],
  'delete-competency-profile': ['competencyProfile'],
  'delete-course': ['course'],
  'delete-course-data': ['course'],
  'delete-course-language-version': ['course'],
  'delete-discussion-comment': ['course'],
  'delete-email-message-template': [],
  'delete-expired-user': ['changelog'],
  'delete-feedback': ['course'],
  'delete-group': ['group'],
  'delete-individual-achievement': ['achievement'],
  'delete-integration': ['integrationChangelog', 'integrationsSharedChangelog'],
  'delete-path': ['path'],
  'delete-path-completion': ['path'],
  'delete-supervisor': ['admin', 'changelog'],
  'delete-user': ['changelog'],
  'delete-user-course-analytics': ['course'],
  'deny-individual-achievement-request': ['achievement'],
  'disable-shared-two-factor-authentication': ['changelog'],
  'disable-two-factor-authentication': ['changelog'],
  'discard-chapter-changes': ['course'],
  'external-add-course-user': ['course'],
  'external-add-group-user': ['group'],
  'external-add-path-user': ['path'],
  'external-add-user': ['changelog'],
  'external-copy-course': ['course'],
  'external-delete-user': ['changelog'],
  'external-mycashflow-order-event': ['changelog', 'course'],
  'external-shopify-order-event': ['changelog', 'course'],
  'external-update-user': ['changelog'],
  'external-woocommerce-order-event': ['changelog', 'course'],
  'fail-background-job': [],
  'generate-ai-translations': ['course'],
  'generate-two-factor-backup-codes': ['changelog'],
  'hubspot-sync-contacts': [],
  'import-achievement': ['achievement'],
  'import-achievement-completions': ['achievement'],
  'import-achievement-template': ['achievement'],
  'import-course-language-version-json': ['course'],
  'join-course-with-link': ['course'],
  'join-path-with-link': ['path'],
  'link-achievement-to-calendar-event': ['calendar'],
  'manual-remove-course-completion': ['course'],
  'manual-set-course-completion': ['course'],
  'merge-users': ['changelog'],
  'pin-competency-profile': ['changelog', 'competencyProfile'],
  'publish-chapter': ['course'],
  'remove-achievement-admin': ['achievement'],
  'remove-achievement-all-product-user-custom-attribute': ['achievement'],
  'remove-achievement-completions': ['achievement'],
  'remove-achievement-owner': ['achievement'],
  'remove-achievement-product-user-custom-attribute': ['achievement'],
  'remove-achievement-users-attachment': ['achievement'],
  'remove-admin-group-user': ['admin'],
  'remove-all-admin-group-users': ['admin'],
  'remove-all-calendar-event-feedback-questions': ['calendar'],
  'remove-all-calendar-event-feedback-template-questions': ['calendar'],
  'remove-all-supervisee': ['admin'],
  'remove-calendar-event-all-product-user-custom-attribute': ['calendar'],
  'remove-calendar-event-attachment': ['calendar'],
  'remove-calendar-event-feedback-question': ['calendar'],
  'remove-calendar-event-feedback-submissions': ['calendar'],
  'remove-calendar-event-feedback-template': ['calendar'],
  'remove-calendar-event-feedback-template-question': ['calendar'],
  'remove-calendar-event-owner': ['calendar'],
  'remove-calendar-event-participation': ['calendar'],
  'remove-calendar-event-product-user-custom-attribute': ['calendar'],
  'remove-calendar-event-self': ['calendar'],
  'remove-calendar-event-users': ['calendar'],
  'remove-client-page-section-attachment': ['clientPage'],
  'remove-competency-admin': ['competency'],
  'remove-competency-profile-admin': ['competencyProfile'],
  'remove-course-admin': ['course'],
  'remove-course-owner': ['course'],
  'remove-course-users': ['course'],
  'remove-email-message-template-product': [],
  'remove-group-admin': ['group'],
  'remove-group-course': ['group'],
  'remove-group-from-supervisor': ['admin'],
  'remove-group-owner': ['group'],
  'remove-group-user': ['group'],
  'remove-path-group': ['path'],
  'remove-path-owner': ['path'],
  'remove-path-step': ['path'],
  'remove-path-users': ['path'],
  'remove-supervisee': ['admin'],
  'remove-user-competency': ['changelog', 'competency'],
  'request-individual-achievement': ['achievement'],
  'retry-failed-course-completion': ['course'],
  'revert-chapter-version': ['course'],
  'revert-course-version': ['course'],
  'save-chapter-changes': ['course'],
  'send-course-group-invites': ['course'],
  'send-group-invites': ['group'],
  'send-message': [],
  'send-path-group-invites': ['path'],
  'send-path-user-invites': ['path'],
  'set-new-password': ['changelog'],
  'set-new-password-admin': ['changelog'],
  'start-background-worker': [],
  'start-new-course-completion': ['course'],
  'submit-calendar-event-feedback': ['calendar'],
  'submit-course-completion-integration': ['course'],
  'transfer-admin-rights': ['admin', 'changelog'],
  'unlink-achievement-from-calendar-event': ['calendar'],
  'unlink-all-achievements-from-calendar-event': ['calendar'],
  'unpin-competency-profile': ['changelog', 'competencyProfile'],
  'unpublish-chapter': ['course'],
  'update-achievement': ['achievement'],
  'update-achievement-access-settings': ['achievement'],
  'update-achievement-admin': ['achievement'],
  'update-achievement-completion-template': ['achievement'],
  'update-achievement-completions': ['achievement'],
  'update-achievement-order-product-user-custom-attribute': ['achievement'],
  'update-achievement-product-user-custom-attribute': ['achievement'],
  'update-achievement-template': ['achievement'],
  'update-admin-group': ['admin'],
  'update-answer': ['course'],
  'update-answer-review': ['course'],
  'update-api-routes': ['apiRoutes', 'integrationsSharedChangelog'],
  'update-auth-strategy': ['authStrategies', 'integrationsSharedChangelog'],
  'update-calendar-event': ['calendar'],
  'update-calendar-event-access-settings': ['calendar'],
  'update-calendar-event-feedback-question': ['calendar'],
  'update-calendar-event-feedback-questions-order': ['calendar'],
  'update-calendar-event-feedback-template': ['calendar'],
  'update-calendar-event-feedback-template-question': ['calendar'],
  'update-calendar-event-feedback-template-questions-order': ['calendar'],
  'update-calendar-event-order-product-user-custom-attribute': ['calendar'],
  'update-calendar-event-product-user-custom-attribute': ['calendar'],
  'update-chapter-slug': ['course'],
  'update-chapters-order': ['course'],
  'update-client-page-section': ['clientPage'],
  'update-competency': ['competency'],
  'update-competency-access-settings': ['competency'],
  'update-competency-admin': ['competency'],
  'update-competency-endorsement': ['changelog', 'competency'],
  'update-competency-profile': ['competencyProfile'],
  'update-competency-profile-access-settings': ['competencyProfile'],
  'update-competency-profile-admin': ['competencyProfile'],
  'update-course': ['course'],
  'update-course-admin': ['course'],
  'update-course-language-version': ['course'],
  'update-course-owner': ['course'],
  'update-course-publish': ['course'],
  'update-course-user-settings': ['course'],
  'update-course-user-settings-for-group': ['course'],
  'update-discussion-comment': ['course'],
  'update-email-message-template': [],
  'update-email-message-template-product': [],
  'update-group': ['group'],
  'update-group-admin': ['group'],
  'update-individual-achievement': ['achievement'],
  'update-integration': ['integrationChangelog', 'integrationsSharedChangelog'],
  'update-password': ['changelog'],
  'update-path': ['path'],
  'update-path-step': ['path'],
  'update-path-step-user-settings': ['path'],
  'update-path-steps-order': ['path'],
  'update-profile': ['changelog'],
  'update-scorm-status': ['course'],
  'update-site-settings': [],
  'update-supervisor': ['admin', 'changelog'],
  'update-user': ['changelog'],
  'update-user-ambitions': ['changelog'],
  'update-user-competencies': ['changelog', 'competency']
} as const satisfies Record<string, readonly EventLogTypeGroup[]>;

export default eventTypeGroups;
